import { useMemo } from "react";
import { Stethoscope, Sparkles, Percent, PiggyBank } from "lucide-react";
import { YesNoInput, ButtonGroupInput, SelectInput, DateInput, SearchableSelectInput, SliderInput } from "../components/fields";
import { Alert } from "../components/Toast";
import { useFormStore } from "../store/formStore";
import { useUiStore } from "../store/uiStore";
import type { useStepValidation } from "../hooks";
import type { SearchOption } from "../components/fields";

const PRIOR_DATE_MIN = new Date(1900, 0, 1);

interface StepCoverageProps {
  validation: ReturnType<typeof useStepValidation>;
}

const MEDICAL_PLAN_TYPE_OPTIONS = [
  { value: "", label: "Select plan type..." },
  { value: "HMO", label: "HMO" },
  { value: "PPO", label: "PPO" },
  { value: "EPO", label: "EPO" },
  { value: "POS", label: "POS" },
  { value: "HDHP", label: "HDHP" },
];

export function StepCoverage({ validation }: StepCoverageProps) {
  const replacingMedical = useFormStore((s) => s.data.replacingMedical);
  const offeringDental = useFormStore((s) => s.data.offeringDental);
  const setField = useFormStore((s) => s.setField);
  const addToast = useUiStore((s) => s.addToast);
  const medicalCarrierMeta = useFormStore((s) => s.meta.medicalCarrierOptions);
  const MEDICAL_CARRIERS: SearchOption[] = medicalCarrierMeta.map(lbl => ({ value: lbl, label: lbl }));

  const priorDateMax = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear() + 5, d.getMonth(), d.getDate());
  }, []);

  const clearMedicalFields = () => {
    setField("priorMedicalCarrier", "");
    setField("priorMedicalCarrierOther", "");
    setField("priorMedicalPlanType", "");
    setField("priorMedicalEffectiveDate", "");
    validation.clearErrors(["priorMedicalCarrier", "priorMedicalCarrierOther", "priorMedicalPlanType", "priorMedicalEffectiveDate"]);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-brand-navy mb-1">Coverage & Benefits</h1>
      <p className="text-gray-500 mb-6">Current coverage and contribution preferences</p>

      {/* Benefit Preferences */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-navy mb-4 flex items-center gap-2">
          <Percent size={20} className="text-gray-500" />
          Benefit Preferences
        </h2>

        <YesNoInput
          field="offeringDental"
          label="Are you offering Optimyl Dental?"
          validation={validation}
          required
          onChange={(v) => {
            // Prior Dental is only relevant when offering Optimyl Dental — clear it when not.
            if (v === "No") {
              setField("replacingDental", "");
              validation.clearErrors(["replacingDental"]);
            }
          }}
        />

        <hr className="my-5 border-gray-200" />

        <YesNoInput
          field="offeringVision"
          label="Are you offering Optimyl Vision?"
          validation={validation}
          required
        />

        <hr className="my-5 border-gray-200" />

        <ButtonGroupInput
          field="deductibleYearType"
          label="Member Deductible Year Type"
          options={["Calendar Year", "Plan Year"]}
          validation={validation}
          required
        />
        <p className="text-xs text-gray-400 mt-1 mb-4">
          <strong>Calendar Year</strong> — deductibles reset January 1st regardless of renewal date.{" "}
          <strong>Plan Year</strong> — deductibles reset on the policy renewal date.
        </p>

        <hr className="my-5 border-gray-200" />

        <Alert
          type="info"
          message='Answer the following contribution question based on the employee-only tier of your least expensive plan. A minimum employer contribution of 50% is required for medical.'
          className="mb-5"
        />

        <SliderInput
          field="medicalContribution"
          label="Employer Medical Contribution"
          min={50}
          max={100}
          hint="Employee-only tier, least expensive plan. Minimum 50% required."
          validation={validation}
          required
        />
      </div>

      {/* Prior Medical Coverage */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-navy mb-4 flex items-center gap-2">
          <Stethoscope size={20} className="text-gray-500" />
          Prior Medical Coverage
        </h2>
        <YesNoInput
          field="replacingMedical"
          label="Will Optimyl replace current group Medical coverage?"
          validation={validation}
          required
          onBeforeChange={(v) => {
            const state = useFormStore.getState();
            const docs = state.data.documents;
            const enrolled = state.data.totalEnrolledEmployees;

            // Collect orphaned files based on what this toggle change would hide
            const orphans: Array<{ key: keyof typeof docs; fileId: string }> = [];
            if (v === "No" && docs.priorBill) {
              orphans.push({ key: "priorBill", fileId: docs.priorBill.fileId });
            }
            const willHideRequiredDocs = v === "Yes" && enrolled !== null && enrolled >= 20;
            if (willHideRequiredDocs) {
              if (docs.wageTax) orphans.push({ key: "wageTax", fileId: docs.wageTax.fileId });
              if (docs.payroll) orphans.push({ key: "payroll", fileId: docs.payroll.fileId });
              if (docs.newBusinessDocs) orphans.push({ key: "newBusinessDocs", fileId: docs.newBusinessDocs.fileId });
            }

            if (orphans.length > 0) {
              // Block the change and ask for confirmation
              addToast(
                "This change will remove your previously uploaded documents on the Documents & Employees page. Would you like to continue?",
                "warning",
                0,
                [
                  { label: "Cancel", onClick: () => {} },
                  {
                    label: "Continue",
                    primary: true,
                    onClick: () => {
                      // Setting replacingMedical triggers the store-level
                      // subscription in formStore.ts which handles clearing
                      // docVerificationType, deleting orphaned files, and
                      // nulling document refs.
                      setField("replacingMedical", v);
                      if (v === "No") clearMedicalFields();
                    },
                  },
                ],
                false,
              );
              return false; // prevent setValue
            }
            return true; // allow setValue
          }}
          onChange={(v) => {
            // Store subscription handles clearing Step 7 fields (docVerificationType, files)
            if (v === "No") clearMedicalFields();
          }}
        />
        {replacingMedical === "Yes" && (
          <div className="mt-4 space-y-4 section-reveal">
            <SearchableSelectInput
              field="priorMedicalCarrier"
              label="Current Carrier Name"
              options={MEDICAL_CARRIERS}
              placeholder="Search carriers..."
              otherField="priorMedicalCarrierOther"
              otherPlaceholder="Enter carrier name..."
              otherMaxLength={40}
              validation={validation}
              required
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectInput
                field="priorMedicalPlanType"
                label="Plan Type"
                options={MEDICAL_PLAN_TYPE_OPTIONS}
                validation={validation}
                required
              />
              <DateInput
                field="priorMedicalEffectiveDate"
                label="Effective Date"
                validation={validation}
                minDate={PRIOR_DATE_MIN}
                maxDate={priorDateMax}
                required
              />
            </div>
          </div>
        )}
      </div>

      {/* Prior Dental Coverage — only relevant when offering Optimyl Dental */}
      {offeringDental === "Yes" && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 section-reveal" aria-live="polite">
          <h2 className="text-lg font-semibold text-brand-navy mb-4 flex items-center gap-2">
            <Sparkles size={20} className="text-gray-500" />
            Prior Dental Coverage
          </h2>
          <YesNoInput
            field="replacingDental"
            label="Will Optimyl replace current group Dental coverage?"
            validation={validation}
            required
          />
        </div>
      )}

      {/* Additional Employer Funding */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-navy mb-4 flex items-center gap-2">
          <PiggyBank size={24} className="text-gray-500 shrink-0 w-6 h-6" />
          Additional Employer Funding and Benefit Plans
        </h2>

        <YesNoInput
          field="reimburseCopays"
          label="Will you reimburse employees for copays, coinsurance, deductibles, or out-of-pocket expenses?"
          validation={validation}
          required
        />

        <hr className="my-5 border-gray-200" />

        <YesNoInput
          field="offerHsa"
          label="Will you offer a Health Savings Account (HSA)?"
          validation={validation}
          required
        />

        <hr className="my-5 border-gray-200" />

        <YesNoInput
          field="offerHra"
          label="Will you offer a Health Reimbursement Account (HRA)?"
          validation={validation}
          required
        />

        <hr className="my-5 border-gray-200" />

        <YesNoInput
          field="otherReimbursement"
          label="Will you use other means to reimburse for out-of-pocket expenses?"
          validation={validation}
          required
        />
      </div>
    </div>
  );
}
